import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Popup from 'src/components/Popup/Popup'
import { useFlyoutDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import {
  OrgExpenseReportConfirmDateChangePopup,
  OrgExpenseReportConfirmDateChangePopupProps,
} from 'src/models/orgExpenseReports.models'
import { createSetConfirmOrgExpenseReportDateChangePopup } from 'src/redux/actions/app.actions'
import { createSetOrgExpenseReportDateUpdateConfirmed } from 'src/redux/actions/data/orgExpenseReports.actions'
import { css } from 'styled-components'

const ConfirmOrgExpenseReportDateChangePopup: React.FC<
  OrgExpenseReportConfirmDateChangePopupProps
> = ({ type, date, rcnName }) => {
  const dispatch = useFlyoutDispatch()

  const body1 = (): React.ReactElement => {
    switch (type) {
      case OrgExpenseReportConfirmDateChangePopup.CreateNewReport:
        return (
          <Trns
            source={
              'You’ve chosen a closing date in the past. This report will include transactions through ${endDate}.'
            }
            expressions={{
              endDate: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {date.format('ll')}
                </span>
              ),
            }}
          />
        )
      case OrgExpenseReportConfirmDateChangePopup.MergeReports:
        return (
          <Trns
            source={
              'You’ve chosen a closing date in the future. ${bolded} that ends on ${endDate}.'
            }
            expressions={{
              bolded: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  <Trns
                    source={
                      'This will merge this report and your current report into one report'
                    }
                  />
                </span>
              ),
              endDate: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {date.format('ll')}
                </span>
              ),
            }}
          />
        )
    }
  }

  const body2 = (): React.ReactElement => {
    switch (type) {
      case OrgExpenseReportConfirmDateChangePopup.CreateNewReport:
        return (
          <Trns
            source={
              'To ensure all calendar days are accounted for, ${bolded} that starts on ${startDate} and closes according to your cycle settings for ${rcnName}.'
            }
            expressions={{
              bolded: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  <Trns
                    source={'this will automatically create a new report'}
                  />
                </span>
              ),
              startDate: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {date.add(1, 'day').format('ll')}
                </span>
              ),
              rcnName: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {rcnName}
                </span>
              ),
            }}
          />
        )
      case OrgExpenseReportConfirmDateChangePopup.MergeReports:
        return (
          <Trns
            source={
              'Your next report for ${rcnName} will be generated on ${startDate}.'
            }
            expressions={{
              rcnName: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {rcnName}
                </span>
              ),
              startDate: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {date.add(1, 'day').format('ll')}
                </span>
              ),
            }}
          />
        )
    }
  }

  const buttonText = (): React.ReactElement => {
    switch (type) {
      case OrgExpenseReportConfirmDateChangePopup.CreateNewReport:
        return <Trns source={'Yes, update this report'} />
      case OrgExpenseReportConfirmDateChangePopup.MergeReports:
        return <Trns source={'Yes, make this my current report'} />
    }
  }

  return (
    <Popup
      noPadding
      onClose={(): void => {
        dispatch(createSetConfirmOrgExpenseReportDateChangePopup(undefined))
      }}
    >
      <div
        css={css`
          position: relative;
          padding: 60px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: ${styleConstants.heights.majorInternalGutter};
          `}
        >
          <Trns source={'Are you sure?'} />
        </div>
        <div
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: ${styleConstants.heights.majorInternalGutter};
            display: flex;
            flex-direction: column;
            gap: 20px;
          `}
        >
          <span>{body1()}</span>
          <span>{body2()}</span>
          <span>
            <Trns
              source={'All transactions will keep their expense review status.'}
            />
          </span>
        </div>
        <div
          css={css`
            display: flex;
            gap: 20px;
            align-items: center;
          `}
        >
          <Button
            testId={'confirmBtn'}
            type={ButtonTypes.button}
            design={ButtonDesigns.designCta}
            big={true}
            text={buttonText()}
            onClick={(): void => {
              dispatch(createSetOrgExpenseReportDateUpdateConfirmed(true))
              dispatch(
                createSetConfirmOrgExpenseReportDateChangePopup(undefined),
              )
            }}
            loading={false}
          />
          <HoverableLink
            testId={'cancelBtn'}
            underline
            ariaLabelSource={'No, let me choose another date'}
            onClick={(): void => {
              dispatch(
                createSetConfirmOrgExpenseReportDateChangePopup(undefined),
              )
            }}
          >
            <Trns source={'No, let me choose another date'} />
          </HoverableLink>
        </div>
      </div>
    </Popup>
  )
}

export default ConfirmOrgExpenseReportDateChangePopup
